import type { HumanoidAppearance } from '../GhostRenderer';
import { npc } from './npc';

/**
 * The Hollow Coil cast on the playable route (content/npcs/hollow_coil.toml +
 * the route NPCs of starter_zone.toml placed by content/zones/*.toml).
 * Palettes come from docs/design/coil-art/NPC_CHARACTER_SHEETS.md and
 * COLOR_PALETTE.md. `humanoid.art` marks the NPCs already on the plan-12
 * finish (docs/plans/2026-10-07-fun-and-polish/12-NPC-ART-UPGRADE.md).
 */
export const HOLLOW_COIL_CAST: Record<string, HumanoidAppearance> = {
  // ── Tomas — Beach Local (Tier 1, warm bright) ──
  // Stocky working man; sun-tan skin, sand-gold tunic, wide straw hat
  // pushed back, hip satchel. The friendly face of the island.
  tomas: npc({
    shape: 'round',
    color: '#d4a868', // sun-tan skin
    eyes: 'happy',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'short', color: '#a87838' }, // sun-bleached brown
      beard: { style: 'stubble', color: '#a87838' },
      headgear: { kind: 'straw_hat', color: '#e8d878', accentColor: '#8b7355' }, // straw + wheat-brown band
      garment: { kind: 'tunic', color: '#f5e3b0', trimColor: '#8b7355' }, // sand-gold tunic, wheat-brown trousers hem
      props: [
        { kind: 'net', color: '#c8b48a' }, // hemp fishing net over the shoulder (sheet: "beach person")
        { kind: 'satchel', color: '#6b4a32' }, // leather brown
      ],
      art: { light: 'lamp', role: 'local' },
    },
  }),

  // ── Merchant Maria — Beach Shopkeeper (Tier 1, warm bright) ──
  // Round, busy shopkeeper; olive-tan skin, dark bun, terracotta apron
  // with potion bottles in the pocket over a marigold tunic. No hat —
  // tomas already owns pig_beach's hat silhouette; apron+bottles is hers.
  merchant_maria: npc({
    shape: 'round',
    color: '#c99a6b', // olive-tan skin (trader's tan, not beach-native)
    eyes: 'happy',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'bun', color: '#2a1f18' }, // near-black brown
      garment: {
        kind: 'apron',
        color: '#c9522f', // terracotta apron
        underColor: '#f0c869', // marigold tunic
        trimColor: '#8b4a2a', // wheat-brown trim
        pocketBottles: true, // the signature identifier — she sells potions
      },
      art: { light: 'lamp', role: 'merchant' },
    },
  }),

  // ── Elder Wynn — Village Elder (Tier 1.5, amber dusk) ──
  // Slight, hunched (small slim body); pale weathered skin, silver hair +
  // short white beard, dusk-purple robe w/ dark trim + pale-mauve collar,
  // leaning cane. Kind but tired.
  elder_wynn: npc({
    shape: 'slim',
    bodySize: 'small',
    color: '#d4c4a8', // pale weathered skin
    eyes: 'sleepy',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'thin', color: '#d4d4d4' }, // silver, receding
      beard: { style: 'short', color: '#d4d4d4' },
      garment: { kind: 'robe', color: '#6b4a6e', trimColor: '#4a3050', underColor: '#a890a0' },
      props: [{ kind: 'cane', color: '#5a3a20' }], // dark wood
      art: { light: 'lamp', role: 'elder' },
    },
  }),

  // ── Maren — Merchant (Tier 1.5, amber warm) ──
  // Sturdy and busy; warm flushed skin, auburn bun, leather apron (with
  // potion bottles in the pocket) over an amber-gold tunic, big satchel.
  maren: npc({
    shape: 'round',
    color: '#d8a890', // warm flushed skin
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'bun', color: '#a8543a' }, // auburn
      garment: {
        kind: 'apron',
        color: '#6b4a32', // leather-brown apron
        underColor: '#d4a060', // amber-gold tunic
        trimColor: '#8b6a4a', // warm-brown skirt hem
        pocketBottles: true,
      },
      props: [{ kind: 'satchel', color: '#5a3a20' }], // dark leather
      art: { light: 'lamp', role: 'merchant' },
    },
  }),

  // ── Sophia — Explorer (Tier 1.5→2, the turn toward the fog) ──
  // Lean; fair weathered skin, dark loose wind-blown hair, dark forest-green
  // cloak (hood down) over an olive tunic, satchel with a map poking out.
  sophia: npc({
    shape: 'slim',
    color: '#d4b8a0', // fair weathered skin
    eyes: 'round',
    mouth: 'neutral', // a slight furrow — she's thinking about the fog
    humanoid: {
      hair: { style: 'loose', color: '#3a2a1a' }, // dark brown
      cloak: { color: '#3a4a3a', claspColor: '#8a7a5a' }, // dark forest green, olive clasp
      garment: { kind: 'tunic', color: '#8a7a5a', trimColor: '#4a3a2a' }, // olive tunic, dark travel pants hem
      props: [{ kind: 'satchel', color: '#5a3a20', mapPeek: true }],
      art: { light: 'lamp', role: 'explorer' },
    },
  }),

  // ── Old Ferris — The Keeper's Legend Teller (Tier 1.5, amber→fog) ──
  // Wide, stocky old sailor — deliberately NOT elder_wynn's frail twin.
  // Salt-dingy grey-white thin hair + full beard (opposite of wynn's
  // short-trimmed one), faded-indigo sweater w/ cream collar trim, belt
  // lantern (the Coil-light he sailed under for 40 years). Reuses
  // NpcProp:'lantern' — ranger_cole's is brackenmoor's one cold-scene warm
  // accent, Ferris's is a warm scene's own light; the two never share a
  // zone, so it isn't a silhouette collision.
  old_ferris: npc({
    shape: 'wide',
    color: '#c9a888', // weathered salt-tan skin
    eyes: 'sleepy',
    mouth: 'neutral', // "it doesn't end well" — not smiling
    humanoid: {
      hair: { style: 'thin', color: '#c4c0ac' }, // salt-dingy grey-white
      beard: { style: 'full', color: '#c4c0ac' }, // full sailor's beard
      garment: { kind: 'tunic', color: '#4a5a6e', trimColor: '#c9beac' }, // faded-indigo sweater, cream collar
      props: [{ kind: 'lantern', glowColor: '#ffcc00' }], // the Coil Light
      art: { light: 'lamp', role: 'local' },
    },
  }),

  // ── Ranger Cole — Gate Warden (Tier 2, cold grey) ──
  // Tall, hard, wrapped up; wind-burned skin (mostly hidden), hood UP with
  // shadowed face + watchful glints, heavy teal-grey cloak, leather armor
  // tunic, longbow + quiver on the back, amber lantern at the belt — the
  // only warm color on him ("the fog hates the light").
  ranger_cole: npc({
    shape: 'slim',
    color: '#c4a890', // wind-burned skin
    eyes: 'round', // hidden by the hood shadow; glints render instead
    mouth: 'neutral',
    humanoid: {
      beard: { style: 'short', color: '#4a3a2a' }, // greying dark beard
      hair: { style: 'short', color: '#4a3a2a', streakColor: '#a0a0a0' }, // under the hood
      headgear: { kind: 'hood', color: '#1a2a2a', accentColor: '#2a3a3a' },
      cloak: { color: '#2a3a3a', claspColor: '#4a3a2a' }, // dark teal-grey
      garment: { kind: 'tunic', color: '#4a3a2a', trimColor: '#3a2a1a' }, // dark leather armor
      props: [
        { kind: 'bow', color: '#5a3a20' }, // dark wood longbow + quiver
        { kind: 'lantern', glowColor: '#ffcc00' },
      ],
    },
  }),

  // ── Warden Lark — First Field survey post (Tier 1→2, the road out) ──
  // The player's first warden: weathered, upright, on watch. A stiff field
  // hat with an amber relay feather, a leather shoulder cape over a
  // warden-green jerkin, and a tall survey spear whose amber pennant is the silhouette
  // identifier — readable across the field against the night sky. Distinct
  // from Ranger Cole (hood, bow, cold teal-grey): open face, warm accents.
  field_warden: npc({
    shape: 'classic',
    color: '#c8a07a', // wind-tanned skin
    eyes: 'round',
    mouth: 'neutral', // "the herd has been bold ever since"
    humanoid: {
      hair: { style: 'short', color: '#7a4a2a' }, // chestnut, cropped under the brim
      headgear: { kind: 'warden_hat', color: '#4a3a28', accentColor: '#e0b860' }, // oiled leather, relay-amber feather
      cloak: { color: '#5a4632', claspColor: '#d8b050' }, // leather shoulder cape, brass survey clasp
      garment: { kind: 'tunic', color: '#4d7a4a', trimColor: '#c89848', beltColor: '#3a2a1c' }, // warden-green jerkin, amber hem
      props: [{ kind: 'spear', color: '#7a5a3a', pennantColor: '#e0a040' }],
      art: { light: 'moon', role: 'warden' },
    },
  }),

  // ── Maelis of the Rim — Moonfall Hollow (the one NPC on the rim) ──
  // Warden Lark's friend who teaches movement: light, lean and wind-blown.
  // Wind-white loose hair with a Moonfall-teal streak, a long teal scarf
  // streaming behind (the silhouette identifier — it moves with the wind),
  // a short slate travel tunic for climbing, and a staff crowned with a
  // Moonfall crystal in the Stone Golem's palette (MOON_MINT).
  rim_keeper: npc({
    shape: 'slim',
    color: '#e0bfa2', // wind-chapped, warm against the white hair
    eyes: 'round',
    mouth: 'smile', // "Light feet. Down you go."
    humanoid: {
      hair: { style: 'loose', color: '#c9d2e2', streakColor: '#5fd8c4' }, // wind-white with a cool cast
      garment: { kind: 'tunic', color: '#3c4f66', trimColor: '#2a3646', beltColor: '#5a4632' },
      props: [
        { kind: 'scarf', color: '#2fb89a', stripeColor: '#a8fff0' },
        { kind: 'crystal_staff', color: '#6a5a48', crystalColor: '#7ffff0' },
      ],
      art: { light: 'moonfall', role: 'keeper' },
    },
  }),

  // ── Elder Aislin — village_arrival (the voice that lit the Vault candles) ──
  // "The candles were lit for you. We've been waiting." The first NPC with
  // lore weight: a calm keeper of the Old Vault, not Wynn's frail twin — no
  // cane, no beard; a silver bun, a deep sea-teal robe with a cream collar
  // and gold trim, and a lit candle held low (the silhouette identifier).
  village_elder: npc({
    shape: 'slim',
    color: '#d8bfa4', // weathered, indoor-pale
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'bun', color: '#e4e0d8' }, // silver-white
      garment: { kind: 'robe', color: '#2f5a66', trimColor: '#c8a050', underColor: '#e8dcc0' },
      props: [{ kind: 'candle', color: '#b08a40' }],
      art: { light: 'lamp', role: 'scholar' },
    },
  }),

  // ── Naturalist Nessa — pig_beach (the one daylight NPC) ──
  // "Mind the tide pools." A sun-tanned field naturalist: round spectacles,
  // dark wind-loose hair, a khaki field tunic, a specimen jar with a tiny
  // crab held up to the light, and a sample satchel.
  beach_naturalist: npc({
    shape: 'slim',
    color: '#c48a62', // sun-tanned
    eyes: 'round',
    mouth: 'smile',
    accessory: 'glasses',
    humanoid: {
      hair: { style: 'loose', color: '#3a2418' },
      garment: { kind: 'tunic', color: '#c9b07a', trimColor: '#6b4a32', beltColor: '#5a3a20' },
      props: [
        { kind: 'satchel', color: '#7a5232' },
        { kind: 'jar', liquidColor: '#4ab08a' },
      ],
      art: { light: 'sun', role: 'scholar' },
    },
  }),
};
